import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import DeliveryMode from '../../store/deliveryMode';
import LoaderStore from '../../store/loader';
import { ActiveProduct, CalledProductCategories } from '../../store/products';
import ProductsCollection from '../../store/productsCollection';
import { earlyInUseEffect } from '../../utils';
import { getProductsCartInfo, viewItemList } from '../../utils/analytics';

import OfferTile from './OfferTile';
import ProductList from './ProductList';
import ProductListItem from './ProductListItem';
import styles from './ProductMenu.module.scss';

export default function ProductMenu({
  slug,
  setNumOfProducts,
  offers,
  categories,
  category,
  subCategory,
  productCollections,
  banners,
  isSticky,
  showBySelection,
  isSecondLevelPage,
  isOfferListPage,
  staticProducts = {},
}) {
  const [productsShowing, setProductsShowing] = useState({});
  const [waveSpecialProducts, setWaveSpecialProducts] = useState([]);
  const [categoriesShowing, setCategoriesShowing] = useState([]);
  const [offersShowing, setOffersShowing] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [infiniteScrollIndex, setInfiniteScrollIndex] = useState(1);
  const [allProductsInCollections, setAllProductsInCollections] = useState();
  const [loading, setLoading] = useState(true);

  function findWaveProductsCategories(parentCategory, products) {
    const productsInCategory = products.filter(p => parentCategory._id === p.category.categoryTree.root);
    const currentSuperPriceProducts = productsInCategory.filter(
      p => p.offerType === 'web-only-amount' || p.offerType === 'web-only-percentage'
    );
    setWaveSpecialProducts(currentSuperPriceProducts);
  }

  function findProductsSubcategory(subcategory, _products, isOffers) {
    if (_products.length === 0) {
      return;
    }
    if (subcategory.subCategories.length !== 0) {
      const productsInCategory = _products.filter(p => subcategory._id === p?.category?.categoryTree?.parent);
      const productsToShow = isOffers
        ? productsInCategory.filter(p => p.offerType !== 'none' && p.offerType !== 'super')
        : productsInCategory;

      setProductsShowing(prevState => ({
        ...prevState,
        [subcategory._id]: prevState[subcategory._id]
          ? [...prevState[subcategory._id], ...productsToShow]
          : productsToShow,
      }));
    }

    const productsInCategory = _products.filter(p => subcategory._id === p?.category?._id);
    const productsToShow = isOffers
      ? productsInCategory.filter(p => p.offerType !== 'none' && p.offerType !== 'super')
      : productsInCategory;
    setProductsShowing(prevState => ({
      ...prevState,
      [subcategory._id]: prevState[subcategory._id]
        ? [...prevState[subcategory._id], ...productsToShow]
        : productsToShow,
    }));
  }

  function findOffersSubCategory(_offers, subcategory, isRootCategory) {
    const offersToShow = _offers.filter(o => {
      const {
        category: { categoryTree },
      } = o;
      if (isRootCategory && categoryTree?.root === subcategory._id) {
        return o;
      }
      if (subcategory?.subCategories.length && categoryTree?.parent === subcategory._id) {
        return o;
      }
      if (o?.category?._id === subcategory?._id) return o;

      return null;
    });
    setOffersShowing(offersToShow);
  }

  function getProductsToShow(currentCategory, products, isOffers = false) {
    if (!currentCategory) return;
    findProductsSubcategory(currentCategory, products, isOffers);
    setLoading(false);
  }

  function loadFunc() {
    if (!categories || !categories.length) return;
    if (categoriesShowing.length && !categoriesShowing[infiniteScrollIndex]) {
      setHasMore(false);
      return;
    }
    if (hasMore && categoriesShowing.length) {
      const _category = categoriesShowing[infiniteScrollIndex];
      const isOffers = slug[0] === 'offers';
      getProductsToShow(_category, allProductsInCollections, isOffers);
      setInfiniteScrollIndex(infiniteScrollIndex + 1);
      CalledProductCategories.update(infiniteScrollIndex + 1);
    }
  }

  function initialLoadFunc(index, _allProductsInCollections, allSubCategories) {
    if (!categories || !categories.length) return;
    if (hasMore && allSubCategories.length) {
      const _category = allSubCategories[index];
      const isOffers = slug[0] === 'offers';
      getProductsToShow(_category, _allProductsInCollections, isOffers);
    }
  }

  function scrollToActiveProduct() {
    setTimeout(() => {
      const elemToScrollTo = document.getElementById(`pr${ActiveProduct.getValue()}`);
      if (elemToScrollTo) {
        elemToScrollTo.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        });
        ActiveProduct.update('');
        LoaderStore.removeEvent('OFFER_SCROLL');
      } else {
        ActiveProduct.update('');
        CalledProductCategories.update(1);
      }
    }, 500);
  }

  useEffect(() => {
    if (!slug) return earlyInUseEffect;
    if (!categories || !categories.length) return earlyInUseEffect;
    if (!productCollections) return earlyInUseEffect;
    if (slug[0] !== 'offers') {
      const service = DeliveryMode.getValue();
      const collectionType = DeliveryMode.getCollectionAsNumber(service) || [];
      const mainCollectionType = collectionType.find(c => c === process.env.defaultCollectionType || c === '751');
      if (!mainCollectionType) return;
      const _allProductsInCollections = ProductsCollection.getProductsForCollections(collectionType);
      setAllProductsInCollections(_allProductsInCollections);

      setInfiniteScrollIndex(1);
      setProductsShowing({});

      const offersInCollection = offers?.filter(o => o.collectionTypes.includes(parseInt(mainCollectionType)));
      const [categorySlug, subCategorySlug] = slug;

      if (subCategorySlug) {
        const tempSubCategory = categories
          .map(c => c.subCategories.find(subC => subC.slugAscii === subCategorySlug && c.slugAscii === categorySlug))
          .filter(c => c !== undefined);
        setHasMore(true);
        setCategoriesShowing(tempSubCategory);
        setNumOfProducts(tempSubCategory[0]?.numberOfProductsToDisplay);
        getProductsToShow(tempSubCategory[0], _allProductsInCollections);
        findOffersSubCategory(offersInCollection, tempSubCategory[0], false);
        return;
      }
      const tempCategory = categories.find(c => c.slugAscii === categorySlug);
      const tempSubCategories = tempCategory.subCategories;
      setHasMore(true);
      setCategoriesShowing(tempSubCategories);
      setNumOfProducts(tempCategory?.numberOfProductsToDisplay);
      findWaveProductsCategories(tempCategory, _allProductsInCollections);

      getProductsToShow(tempSubCategories[0], _allProductsInCollections);
      findOffersSubCategory(offersInCollection, tempCategory, true);
      ActiveProduct.update('');
      CalledProductCategories.update(1);
    }
  }, [categories, productCollections, slug]);

  useEffect(() => {
    if (!slug) return earlyInUseEffect;
    if (!categories || !categories.length) return earlyInUseEffect;
    if (!productCollections) return earlyInUseEffect;
    if (slug[0] === 'offers') {
      const service = DeliveryMode.getValue();
      const collectionType = DeliveryMode.getCollectionAsNumber(service) || [];
      const mainCollectionType = collectionType.find(c => c === process.env.defaultCollectionType || c === '751');
      if (!mainCollectionType) return;
      const _allProductsInCollections = ProductsCollection.getProductsForCollections(collectionType);
      setAllProductsInCollections(_allProductsInCollections);
      setProductsShowing({});

      setInfiniteScrollIndex(1);

      const offersInCollection = offers?.filter(o => o.collectionTypes.includes(parseInt(mainCollectionType)));
      const [categorySlug] = slug;

      if (categorySlug === 'offers') {
        const allSubCategories = categories.flatMap(c => c.subCategories);
        setHasMore(true);
        setOffersShowing(offersInCollection);
        setCategoriesShowing(allSubCategories);
        getProductsToShow(allSubCategories[0], _allProductsInCollections, true);
        if (CalledProductCategories.getValue() !== 1 && ActiveProduct.getValue() !== '') {
          LoaderStore.addEvent('OFFER_SCROLL');
          for (let i = 1; i < CalledProductCategories.getValue(); i++) {
            initialLoadFunc(i, _allProductsInCollections, allSubCategories);
          }
          setInfiniteScrollIndex(CalledProductCategories.getValue());
          scrollToActiveProduct();
        } else {
          CalledProductCategories.update(1);
        }
      }
    }
  }, [categories, productCollections]);

  const productShowingLength = Object.keys(productsShowing).length;

  useEffect(() => {
    // send analytics view_item_list for every new category added to the viewport
    if (productShowingLength > 0) {
      const lastCategory = Object.keys(productsShowing)[productShowingLength - 1];

      viewItemList(getProductsCartInfo(productsShowing[lastCategory]), {
        item_list_id: category?._id,
        item_list_name: category?.name,
      });
    }
  }, [productShowingLength]);

  const offersNotEmpty = offersShowing.length !== 0;
  const waveSpecialProductsNotEmpty = waveSpecialProducts.length !== 0;

  // Code for bots
  if (isEmpty(categoriesShowing)) {
    return (
      <section className={styles.visibilityHidden}>
        <div className={styles.listHeader}>
          <h1 className={styles.listTitle}>{isEmpty(subCategory) ? category?.name : subCategory?.name}</h1>
        </div>
        {Object.values(staticProducts).map((productsArray, index) => (
          <div key={Object.keys(staticProducts)[index]}>
            {isEmpty(subCategory) && (
              <div className={styles.listHeader}>
                <h2 className={styles.listTitle}>{category?.subCategories?.[index]?.name}</h2>
              </div>
            )}
            {productsArray.map((product, pIndex) => (
              <ProductListItem
                key={product._id}
                _product={product}
                itemListName={isEmpty(subCategory) ? category?.name : subCategory?.name}
                itemListId={isEmpty(subCategory) ? category?.name : subCategory?.name}
                positionInList={pIndex + 1}
              />
            ))}
          </div>
        ))}
      </section>
    );
  }

  return (
    <section className={styles.listContainer}>
      <div className={cn(styles.infiniteList, { [styles.notSticky]: !isSticky })}>
        {!isOfferListPage &&
          ((!isSecondLevelPage && (waveSpecialProductsNotEmpty || offersNotEmpty)) ||
            (isSecondLevelPage && offersNotEmpty)) && (
            <div>
              <div className={styles.listHeader}>
                <p className={styles.listTitle}>Σας συμφέρει</p>
              </div>
              <div className={styles.bannerContainer}>
                {!isSecondLevelPage &&
                  waveSpecialProducts.map((product, index) => (
                    <ProductListItem
                      key={product._id}
                      _product={product}
                      {...{ categories }}
                      positionInList={index + 1}
                    />
                  ))}

                {offersShowing.map(o => (
                  <OfferTile key={o._id} offer={o} />
                ))}
              </div>
            </div>
        )}
        {categoriesShowing && categoriesShowing.length !== 0 && Object.values(productsShowing).length !== 0 && (
          <InfiniteScroll
            threshold={440} // 440px is the height of a category with 1 row of products
            hasMore={hasMore}
            // keep it like this until we purge the logic of multiple async calls to local db for products
            initialLoad={false}
            loader={<div key="0" />}
            loadMore={loadFunc}
          >
            {Object.values(productsShowing).map((products, i) => (
              <ProductList
                key={categoriesShowing[i]._id}
                products={products}
                category={categoriesShowing[i]}
                categoriesShowing={categoriesShowing}
                slug={slug}
                banners={banners}
                showBySelection={showBySelection}
                isSecondLevelPage={isSecondLevelPage}
                categories={categories}
                loading={loading}
                itemListId={category?._id}
                itemListName={category?.name}
              />
            ))}
          </InfiniteScroll>
        )}
      </div>
    </section>
  );
}
